/**
 * The official-address block of the property Address tab (card layout), with
 * the two-step "Change address" flow:
 *
 *   view   — pin tile, the address, a Geocode verified / Not verified chip,
 *            "Official address · County", and Copy / Retry verification /
 *            Change address.
 *   search — 1 Find address: Google Places autocomplete. Picking a suggestion
 *            goes straight to review. "Enter it manually" switches to manual.
 *   manual — 1 Find address: street number / name / unit / city / state / ZIP.
 *            "Review change" first checks the typed address against Places so
 *            the review can say whether it will verify.
 *   review — 2 Review and save: Current → New cards, a per-field diff, the
 *            no-match warning, and "Also update the address on title" (offered
 *            when the title address currently matches the official one).
 *
 * Saves (PROPERTY_ADDRESS_GUIDE.md):
 *   - verified (a Places pick, or a manual entry that matched) →
 *     POST create_from_string with the Places formatted address;
 *   - unmatched manual entry → POST create_from_object with the typed parts
 *     (the backend still tries to geocode, and saves as entered if it can't);
 *   - a unit number is re-attached afterwards via update_subject_property —
 *     the geocoder never returns one and both endpoints whole-replace;
 *   - "Also update the address on title" then writes `stringAddressFinalTitle`
 *     from the address the backend actually saved.
 *
 * "Retry verification" re-sends the saved address through create_from_string.
 */
import {
  ArrowForwardRounded,
  CheckRounded,
  ContentCopyOutlined,
  EditOutlined,
  ErrorOutlineRounded,
  InfoOutlined,
  LocationOnOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import { useAddressHook } from "../../../hooks/useAddressHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import { amber, ink, slate, status, teal } from "../../../theme/colors";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

const PLACES_AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_URL = "https://places.googleapis.com/v1/places";
const LINE = slate[200];

/* ── helpers ──────────────────────────────────────────── */

/**
 * "verified" | "partial" | "failed" | "none". The backend's geocoder (Azure
 * Maps) never writes `geolocation` itself, so an unset value with coordinates
 * counts as verified; this flow writes "match" / "partial" / "failed"
 * explicitly after a change (see `save`).
 */
export const geocodeState = (a) => {
  if (!a) return "none";
  if (a.geolocation === "partial") return "partial";
  if (a.geolocation === "failed") return "failed";
  return a.latitude != null || a.longitude != null ? "verified" : "failed";
};
export const isGeocodeVerified = (a) => geocodeState(a) === "verified";

// Street comparison that ignores the geocoder's spelling: "2918 West
// Mooresville Road" = "2918 W Mooresville Rd", but ≠ "2918 S Mooresville Rd".
const STREET_ABBR = {
  north: "n", south: "s", east: "e", west: "w",
  northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
  road: "rd", street: "st", avenue: "ave", av: "ave", drive: "dr", court: "ct",
  lane: "ln", boulevard: "blvd", place: "pl", terrace: "ter", circle: "cir",
  parkway: "pkwy", highway: "hwy", trail: "trl", square: "sq", point: "pt",
};

// "14 Balsam Court, Wantagh, NY 11793, USA" ≈ "14 Balsam Ct, Wantagh, NY 11793":
// case, punctuation, a trailing country and suffix / directional spelling
// (Avenue = Ave, Southwest = SW) don't make two addresses differ.
export const normalizeAddress = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/,?\s*(usa|united states)\s*$/, "")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((t) => STREET_ABBR[t] || t)
    .join(" ");

/**
 * Street number + name for display. Some write paths (e.g. the Salesforce
 * acquisitions sync) store the whole street line in `streetName` and leave
 * `streetNumber` empty — "729 SW 15th Ave" — so peel a leading house number
 * off when the number field is blank.
 */
export const splitStreet = (a) => {
  const number = String(a?.streetNumber ?? "").trim();
  const name = String(a?.streetName ?? "").trim();
  if (number || !name) return { streetNumber: number, streetName: name };
  const m = name.match(/^(\d+[A-Za-z]?(?:[-/]\d+[A-Za-z]?)?)\s+(.+)$/);
  return m ? { streetNumber: m[1], streetName: m[2] } : { streetNumber: "", streetName: name };
};
const canonStreet = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => STREET_ABBR[t] || t)
    .join(" ");
const streetOf = (a) =>
  a?.streetNumber || a?.streetName
    ? `${a.streetNumber || ""} ${a.streetName || ""}`
    : String(a?.fullAddress || a?.streetAddress || "").split(",")[0];
const sameStreet = (a, b) => {
  const sa = canonStreet(streetOf(a));
  return Boolean(sa) && sa === canonStreet(streetOf(b));
};
const zip5 = (z) => String(z || "").trim().slice(0, 5);

const comp = (components, type, key = "longText") =>
  components?.find((c) => c.types?.includes(type))?.[key] || "";

const placeToDraft = (place) => {
  const c = place?.addressComponents || [];
  return {
    streetNumber: comp(c, "street_number", "shortText"),
    streetName: comp(c, "route"),
    unit: comp(c, "subpremise", "shortText"),
    city: comp(c, "locality") || comp(c, "sublocality") || comp(c, "postal_town"),
    state: comp(c, "administrative_area_level_1", "shortText"),
    zip: comp(c, "postal_code", "shortText"),
    county: comp(c, "administrative_area_level_2"),
    fullAddress: place?.formattedAddress || "",
  };
};

const draftLabel = (d) => {
  const street = [d.streetNumber, d.streetName].filter(Boolean).join(" ");
  const line1 = [street, d.unit ? `Unit ${d.unit}` : ""].filter(Boolean).join(" ");
  const stateZip = [d.state, d.zip].filter(Boolean).join(" ");
  return [line1, d.city, stateZip].filter(Boolean).join(", ");
};

const fromSaved = (a) => ({
  ...splitStreet(a),
  unit: a?.addressUnitNumber || a?.unit || "",
  city: a?.city || "",
  state: a?.state || "",
  zip: a?.zip || "",
  county: a?.county || "",
});

const DIFF_FIELDS = [
  ["streetNumber", "Street number"],
  ["streetName", "Street name"],
  ["unit", "Unit"],
  ["city", "City"],
  ["state", "State"],
  ["zip", "ZIP"],
  ["county", "County"],
];

// The hooks are callback-style; the save is a short chain, so wrap them.
// `onCompleteFn` without a success/fail first means the call bailed out.
const call = (fn, args) =>
  new Promise((resolve, reject) => {
    let settled = false;
    fn({
      ...args,
      onSuccessFn: (r) => {
        settled = true;
        resolve(r);
      },
      onFailFn: (e) => {
        settled = true;
        reject(e || new Error("failed"));
      },
      onCompleteFn: () =>
        setTimeout(() => {
          if (!settled) reject(new Error("failed"));
        }, 0),
    });
  });

/* ── small pieces ─────────────────────────────────────── */

const neutralBtnSx = {
  flexShrink: 0,
  bgcolor: "#fff",
  color: slate[700],
  borderColor: slate[300],
  fontWeight: 500,
  "&:hover": { bgcolor: slate[50], borderColor: slate[400] },
};

const linkSx = {
  fontSize: 13,
  fontWeight: 500,
  color: teal[700],
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

const LABEL_SX = { fontSize: 13, fontWeight: 500, color: slate[700], mb: "6px" };
const INPUT_SX = { "& .MuiInputBase-root": { height: 40, fontSize: 14, background: "#fff" } };

const Field = ({ label, optional, children, sx }) => (
  <Flx column sx={{ minWidth: 0, ...sx }}>
    <Txt component="label" sx={LABEL_SX}>
      {label}
      {optional ? (
        <Box component="span" sx={{ color: slate[400], fontWeight: 400, ml: "4px" }}>
          optional
        </Box>
      ) : null}
    </Txt>
    {children}
  </Flx>
);
Field.propTypes = { label: PropTypes.string, optional: PropTypes.bool, children: PropTypes.node, sx: PropTypes.object };

const Steps = ({ step }) => {
  const review = step === "review";
  return (
    <Flx ac sx={{ gap: "6px", fontSize: 12.5, flexShrink: 0 }}>
      <Box component="span" sx={{ color: review ? slate[700] : teal[700], fontWeight: 500 }}>
        1 Find address
      </Box>
      <Box component="span" sx={{ color: slate[400] }}>›</Box>
      <Box component="span" sx={{ color: review ? teal[700] : slate[500], fontWeight: 500 }}>
        2 Review and save
      </Box>
    </Flx>
  );
};
Steps.propTypes = { step: PropTypes.string };

const PanelHead = ({ title, step, current }) => (
  <Flx column sx={{ gap: "6px" }}>
    <Flx ac jb wrap sx={{ gap: "6px 16px" }}>
      <Txt component="span" sx={{ fontSize: 15, fontWeight: 600, color: ink }}>
        {title}
      </Txt>
      <Steps step={step} />
    </Flx>
    {current ? (
      <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
        Current:{" "}
        <Box component="span" sx={{ color: slate[800], fontWeight: 500 }}>
          {current}
        </Box>
      </Txt>
    ) : null}
  </Flx>
);
PanelHead.propTypes = { title: PropTypes.string, step: PropTypes.string, current: PropTypes.string };

const VERIFY_LABEL = {
  verified: "Geocode verified",
  partial: "Approximate location",
  failed: "Not verified",
};

const VerifyChip = ({ state, warning }) => {
  const verified = state === "verified";
  const chip = (
    <Flx
      ac
      sx={{
        gap: "4px",
        px: "8px",
        py: "1px",
        borderRadius: "999px",
        background: verified ? status.success.bg : amber.light,
        color: verified ? status.success.fg : amber.chipText,
        fontSize: 12,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {verified ? (
        <CheckRounded sx={{ fontSize: "13px !important" }} />
      ) : (
        <ErrorOutlineRounded sx={{ fontSize: "13px !important" }} />
      )}
      {VERIFY_LABEL[state] || VERIFY_LABEL.failed}
    </Flx>
  );
  return warning ? (
    <Tooltip title={warning} arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};
VerifyChip.propTypes = { state: PropTypes.string, warning: PropTypes.string };

// An inline text link — a span, not a <button>, so it sits on the sentence's
// baseline instead of riding a few px off it.
const TextLink = ({ onClick, disabled, children }) => (
  <Box
    component="span"
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{ ...linkSx, ...(disabled ? { opacity: 0.5, pointerEvents: "none" } : {}) }}
  >
    {children}
  </Box>
);
TextLink.propTypes = { onClick: PropTypes.func, disabled: PropTypes.bool, children: PropTypes.node };

const AddressCard = ({ tone, label, address, sub }) => (
  <Flx
    column
    sx={{
      flex: "1 1 220px",
      minWidth: 0,
      gap: "4px",
      p: "12px 14px",
      borderRadius: "8px",
      background: "#fff",
      border: `1px solid ${tone === "new" ? teal[300] : LINE}`,
      boxShadow: tone === "new" ? `0 0 0 3px ${teal[50]}` : "none",
    }}
  >
    <Flx ac sx={{ gap: "6px", fontSize: 12, color: tone === "new" ? teal[700] : slate[500] }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: tone === "new" ? teal[600] : slate[400] }} />
      {label}
    </Flx>
    <Txt component="div" sx={{ fontSize: 14, fontWeight: 600, color: ink, wordBreak: "break-word" }}>
      {address || "—"}
    </Txt>
    <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
      {sub}
    </Txt>
  </Flx>
);
AddressCard.propTypes = { tone: PropTypes.string, label: PropTypes.string, address: PropTypes.string, sub: PropTypes.string };

/* ── the block ────────────────────────────────────────── */

const PropertyOfficialAddress = ({ property, loanId, onEditingChange }) => {
  const dispatch = useDispatch();
  const { createSubjectPropertyFromString, createSubjectPropertyFromObject } = useAddressHook();
  const { updateSubjectProperty } = useUnderwritingHook();
  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const propertyId = property?._id;
  const saved = property?.address;
  const current = addressLabel(saved, "");
  const geoState = geocodeState(saved);
  const verified = geoState === "verified";
  const titleAddress = property?.stringAddressFinalTitle || "";
  const titleMatches =
    Boolean(titleAddress) && Boolean(current) && normalizeAddress(titleAddress) === normalizeAddress(current);

  const [step, setStep] = useState("view"); // view | search | manual | review
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manual, setManual] = useState(fromSaved(saved));
  const [draft, setDraft] = useState(null); // { ...parts, fullAddress, verified, source }
  const [alsoTitle, setAlsoTitle] = useState(true);
  const [busy, setBusy] = useState(false); // checking or saving
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    onEditingChange?.(step !== "view");
  }, [step, onEditingChange]);

  // A different property never inherits a half-finished change.
  useEffect(() => {
    setStep("view");
    setDraft(null);
    setError(null);
  }, [propertyId]);

  const start = () => {
    setInputValue("");
    setOptions([]);
    setManual(fromSaved(saved));
    setDraft(null);
    setAlsoTitle(true);
    setError(null);
    setStep("search");
  };

  const cancel = () => {
    setStep("view");
    setDraft(null);
    setError(null);
  };

  /* Places ──────────────────────────────────────────── */

  const autocomplete = useCallback(
    async (query) => {
      const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Goog-Api-Key": googleMapsKey },
        body: JSON.stringify({
          input: query,
          includedRegionCodes: ["us"],
          includedPrimaryTypes: ["street_address", "premise", "subpremise", "route"],
        }),
      });
      const data = await res.json();
      return (data.suggestions || [])
        .map((s) => s.placePrediction)
        .filter(Boolean)
        .map((p) => ({
          placeId: p.placeId,
          fullAddress: p.text?.text || "",
          mainText: p.structuredFormat?.mainText?.text || "",
          secondaryText: p.structuredFormat?.secondaryText?.text || "",
        }));
    },
    [googleMapsKey],
  );

  const details = useCallback(
    async (placeId) => {
      const res = await fetch(`${PLACES_DETAILS_URL}/${placeId}`, {
        headers: {
          "X-Goog-Api-Key": googleMapsKey,
          "X-Goog-FieldMask": "id,formattedAddress,addressComponents",
        },
      });
      return res.json();
    },
    [googleMapsKey],
  );

  const runSearch = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.length < 3 || !googleMapsKey) {
          setOptions([]);
          return;
        }
        setSearching(true);
        try {
          setOptions(await autocomplete(q));
        } catch (err) {
          console.error("Places autocomplete error", err);
          setOptions([]);
        } finally {
          setSearching(false);
        }
      }, 300),
    [autocomplete, googleMapsKey],
  );

  useEffect(() => {
    if (step === "search") runSearch(inputValue);
    return () => runSearch.cancel();
  }, [inputValue, step, runSearch]);

  const handlePick = async (_e, option) => {
    if (!option?.placeId) return;
    setBusy(true);
    setError(null);
    try {
      const place = placeToDraft(await details(option.placeId));
      setDraft({ ...place, verified: true, source: "search" });
      setStep("review");
    } catch (err) {
      console.error("Place details error", err);
      setError("Couldn't load that address. Try again, or enter it manually.");
    } finally {
      setBusy(false);
    }
  };

  // Manual → review: look the typed address up in Places; a hit on the same
  // street number + ZIP counts as a match and takes the normalized parts.
  const reviewManual = async () => {
    const m = { ...manual, state: (manual.state || "").toUpperCase() };
    if (!m.streetNumber.trim() || !m.streetName.trim()) {
      setError("Street number and street name are required.");
      return;
    }
    if (!m.city.trim() || !m.state.trim() || !m.zip.trim()) {
      setError("City, state and ZIP are required.");
      return;
    }
    setError(null);
    setBusy(true);
    let next = { ...m, county: "", fullAddress: "", verified: false, source: "manual" };
    try {
      if (googleMapsKey) {
        const [top] = await autocomplete(draftLabel({ ...m, unit: "" }));
        if (top?.placeId) {
          const place = placeToDraft(await details(top.placeId));
          // Same street (directional and suffix included) and ZIP — a near
          // miss like S vs W Mooresville Rd is NOT a match.
          const sameZip = place.zip ? zip5(place.zip) === zip5(m.zip) : true;
          if (sameStreet(place, m) && sameZip) {
            next = { ...place, unit: m.unit.trim() || place.unit, verified: true, source: "manual" };
          }
        }
      }
    } catch (err) {
      console.error("Address check failed", err);
    } finally {
      setBusy(false);
    }
    setDraft(next);
    setStep("review");
  };

  /* save ────────────────────────────────────────────── */

  const finish = (response) => {
    dispatch(loanDrilldownSet(response));
    dispatch(loanPipelineClear(response));
  };

  const idOf = (v) => String(v?.$oid ?? v ?? "");
  const savedPropertyOf = (deal) =>
    (deal?.subjectProperties || []).find((p) => idOf(p?._id) === idOf(propertyId));

  /**
   * Write `d` as the official address. Two calls:
   *
   *  1. create_from_string (a verified pick) or create_from_object (typed
   *     parts). Both geocode with Azure Maps and WHOLE-REPLACE `address` with
   *     whatever Azure returns — formatted `fullAddress`, `addressLine`, zip,
   *     county, state, city, coordinates. Azure never returns street number /
   *     name / unit, and it fuzzy-snaps a street it doesn't know to the
   *     nearest one it does (2918 S Mooresville Rd → 2918 W Mooresville Rd),
   *     so `fullAddress` — which every surface reads first — can come back as
   *     a different address from the one entered.
   *  2. update_subject_property (deep-flattened `$set`, so siblings survive)
   *     makes the confirmed parts authoritative: street number / name, city,
   *     state, ZIP, unit. If Azure's street disagrees with what was entered,
   *     it also writes `fullAddress` / `displayAddress` back to the entered
   *     address and records the downgrade on the model's own fields —
   *     `geolocation: "partial"` (coordinates are the geocoder's best guess)
   *     or `"failed"` (none), with `geocode_warning` naming what the geocoder
   *     matched. Otherwise `geolocation: "match"`. The title address rides
   *     along in the same call when asked.
   */
  const commitAddress = async (d, { updateTitle }) => {
    const unit = (d.unit || "").trim();
    const parts = {
      streetNumber: (d.streetNumber || "").trim(),
      streetName: (d.streetName || "").trim(),
      city: (d.city || "").trim(),
      state: (d.state || "").trim().toUpperCase(),
      zip: (d.zip || "").trim(),
    };
    const hasParts = Boolean(parts.streetNumber && parts.streetName);

    let deal =
      d.verified && d.fullAddress
        ? await call(createSubjectPropertyFromString, {
            dealId: loanId,
            subjectPropertyId: propertyId,
            addressString: d.fullAddress,
          })
        : hasParts
          ? await call(createSubjectPropertyFromObject, {
              dealId: loanId,
              subjectPropertyId: propertyId,
              addressObject: { ...parts, ...(unit ? { addressUnitNumber: unit, unit } : {}) },
            })
          : await call(createSubjectPropertyFromString, {
              dealId: loanId,
              subjectPropertyId: propertyId,
              addressString: d.fullAddress || draftLabel(d),
            });
    finish(deal);

    const geo = savedPropertyOf(deal)?.address || {};
    const entered = draftLabel({ ...parts, unit });
    const hasCoords = geo.latitude != null && geo.longitude != null;
    const matched = !hasParts || (sameStreet(geo, parts) && (!geo.zip || zip5(geo.zip) === zip5(parts.zip)));

    const address = {
      ...(hasParts ? parts : {}),
      ...(unit ? { addressUnitNumber: unit, unit } : {}),
      ...(!geo.county && d.county ? { county: d.county } : {}),
      ...(matched && hasCoords
        ? { geolocation: "match", geocode_warning: null }
        : {
            fullAddress: entered,
            displayAddress: entered,
            geolocation: hasCoords ? "partial" : "failed",
            geocode_warning: hasCoords
              ? `Geocoder matched "${geo.fullAddress || "a different address"}"; kept the address as entered. Confirm the map pin.`
              : "No geocode match; saved as entered.",
          }),
    };
    const data = { address };
    if (updateTitle) {
      data.stringAddressFinalTitle = matched && hasCoords ? addressLabel({ ...geo, ...address }, entered) : entered;
    }
    deal = await call(updateSubjectProperty, { loanId, propertyId, data });
    finish(deal);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await commitAddress(draft, { updateTitle: titleMatches && alsoTitle });
      setStep("view");
      setDraft(null);
    } catch (err) {
      setError("Couldn't save the address. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Re-geocodes the address AS SAVED — its own parts, never Azure's last
  // guess — so a deliberate manual address isn't snapped back.
  const retryVerification = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await commitAddress(
        { ...fromSaved(saved), fullAddress: current, verified: false },
        { updateTitle: false },
      );
    } catch (err) {
      // the hook already surfaced the error
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(current).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  /* render ──────────────────────────────────────────── */

  if (step === "view") {
    return (
      <Flx ac wrap sx={{ gap: "12px 16px", px: 2.5, py: 2 }}>
        <Flx ac sx={{ gap: "14px", flex: "1 1 320px", minWidth: 0 }}>
          <Flx
            center
            sx={{
              width: 38,
              height: 38,
              borderRadius: "9px",
              background: current ? teal[100] : slate[100],
              flexShrink: 0,
            }}
          >
            <LocationOnOutlined sx={{ fontSize: 20, color: current ? teal[700] : slate[400] }} />
          </Flx>
          <Flx column sx={{ gap: "4px", minWidth: 0 }}>
            <Txt
              component="div"
              sx={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.3,
                wordBreak: "break-word",
                color: current ? ink : slate[500],
                fontStyle: current ? "normal" : "italic",
              }}
            >
              {current || "No address set"}
            </Txt>
            {current ? (
              <Flx ac wrap sx={{ gap: "4px 10px" }}>
                <VerifyChip state={geoState} warning={saved?.geocode_warning || undefined} />
                <Txt component="span" sx={{ fontSize: 12, color: slate[500] }}>
                  Official address · {saved?.county || "County unknown"}
                </Txt>
              </Flx>
            ) : null}
          </Flx>
        </Flx>
        <Flx ac wrap sx={{ gap: 1, ml: "auto" }}>
          {current ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={copied ? <CheckRounded /> : <ContentCopyOutlined />}
              onClick={copy}
              sx={neutralBtnSx}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : null}
          {current && !verified ? (
            <Button size="small" variant="outlined" disabled={busy} onClick={retryVerification} sx={neutralBtnSx}>
              {busy ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : "Retry verification"}
            </Button>
          ) : null}
          <Button size="small" variant="outlined" startIcon={<EditOutlined />} onClick={start} sx={neutralBtnSx}>
            {current ? "Change address" : "Add address"}
          </Button>
        </Flx>
      </Flx>
    );
  }

  const panelSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    px: 2.5,
    py: 2.25,
    background: slate[50],
    borderTop: `2px solid ${teal[500]}`,
  };

  const errorLine = error ? (
    <Flx ac sx={{ gap: "6px", fontSize: 12.5, color: status.error.fg }}>
      <ErrorOutlineRounded sx={{ fontSize: 15 }} />
      {error}
    </Flx>
  ) : null;

  if (step === "search") {
    return (
      <Box sx={panelSx}>
        <PanelHead title={current ? "Change official address" : "Add official address"} step={step} current={current} />
        <Autocomplete
          freeSolo
          openOnFocus
          filterOptions={(x) => x}
          options={options}
          loading={searching}
          disabled={busy}
          inputValue={inputValue}
          onInputChange={(_e, val, reason) => {
            if (reason === "input" || reason === "clear") setInputValue(val);
          }}
          onChange={handlePick}
          getOptionLabel={(o) => (typeof o === "string" ? o : o?.fullAddress || "")}
          isOptionEqualToValue={(o, v) => o?.placeId === v?.placeId}
          noOptionsText={inputValue.length < 3 ? "Keep typing…" : "No matches — try entering it manually"}
          renderOption={(props, o) => (
            <li {...props} key={o.placeId}>
              <Box>
                <Txt component="div" sx={{ fontSize: 13.5, fontWeight: 500, color: ink }}>
                  {o.mainText || o.fullAddress}
                </Txt>
                {o.secondaryText ? (
                  <Txt component="div" sx={{ fontSize: 12, color: slate[500] }}>
                    {o.secondaryText}
                  </Txt>
                ) : null}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Start typing a street address"
              onKeyDown={(e) => e.key === "Escape" && cancel()}
              sx={{ "& .MuiInputBase-root": { height: 44, fontSize: 14.5, background: "#fff" } }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={{ fontSize: 19, color: slate[400] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {searching || busy ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {errorLine}
        <Flx ac jb sx={{ gap: 2 }}>
          <Txt component="span" sx={{ fontSize: 13, color: slate[500] }}>
            Can&apos;t find it?{" "}
            <TextLink
              onClick={() => {
                setError(null);
                setStep("manual");
              }}
            >
              Enter it manually
            </TextLink>
          </Txt>
          <Button variant="outlined" onClick={cancel} sx={neutralBtnSx}>
            Cancel
          </Button>
        </Flx>
      </Box>
    );
  }

  if (step === "manual") {
    const set = (k) => (e) => setManual((m) => ({ ...m, [k]: e.target.value }));
    const onKeyDown = (e) => {
      if (e.key === "Enter") reviewManual();
      if (e.key === "Escape") cancel();
    };
    return (
      <Box sx={panelSx}>
        <PanelHead title="Enter address manually" step={step} current={current} />
        <Flx column sx={{ gap: 2 }} onKeyDown={onKeyDown}>
          <Flx wrap sx={{ gap: "16px 12px" }}>
            <Field label="Street number" sx={{ flex: "0 0 120px" }}>
              <TextField size="small" value={manual.streetNumber} onChange={set("streetNumber")} sx={INPUT_SX} autoFocus />
            </Field>
            <Field label="Street name" sx={{ flex: "1 1 220px" }}>
              <TextField size="small" value={manual.streetName} onChange={set("streetName")} sx={INPUT_SX} />
            </Field>
            <Field label="Unit" optional sx={{ flex: "0 0 160px" }}>
              <TextField size="small" value={manual.unit} onChange={set("unit")} placeholder="Apt, suite" sx={INPUT_SX} />
            </Field>
          </Flx>
          <Flx wrap sx={{ gap: "16px 12px" }}>
            <Field label="City" sx={{ flex: "1 1 220px" }}>
              <TextField size="small" value={manual.city} onChange={set("city")} sx={INPUT_SX} />
            </Field>
            <Field label="State" sx={{ flex: "0 0 86px" }}>
              <TextField
                select
                size="small"
                value={manual.state || ""}
                onChange={set("state")}
                sx={INPUT_SX}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
              >
                {selectOptionsUsaStates.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                    {o.value}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
            <Field label="ZIP" sx={{ flex: "0 0 86px" }}>
              <TextField size="small" value={manual.zip} onChange={set("zip")} inputProps={{ inputMode: "numeric" }} sx={INPUT_SX} />
            </Field>
          </Flx>
          <Flx ac sx={{ gap: "8px", fontSize: 12.5, color: slate[500] }}>
            <InfoOutlined sx={{ fontSize: 15 }} />
            We&apos;ll check this against the geocoder before saving. If there&apos;s no match you can still save it,
            and it will be marked as not verified.
          </Flx>
        </Flx>
        {errorLine}
        <Flx ac jb sx={{ gap: 2 }}>
          <TextLink
            onClick={() => {
              setError(null);
              setStep("search");
            }}
          >
            Search instead
          </TextLink>
          <Flx ac sx={{ gap: 1 }}>
            <Button variant="outlined" onClick={cancel} disabled={busy} sx={neutralBtnSx}>
              Cancel
            </Button>
            <Button variant="contained" onClick={reviewManual} disabled={busy}>
              {busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Review change"}
            </Button>
          </Flx>
        </Flx>
      </Box>
    );
  }

  // review
  const before = fromSaved(saved);
  const changes = DIFF_FIELDS.filter(([k]) => (before[k] || "") !== (draft?.[k] || ""));
  const newLabel = draft ? (draft.verified && draft.fullAddress ? addressLabel({ fullAddress: draft.fullAddress }, "") || draftLabel(draft) : draftLabel(draft)) : "";

  return (
    <Box sx={panelSx}>
      <PanelHead title="Review address change" step={step} />
      <Flx ac wrap sx={{ gap: "10px" }}>
        <AddressCard tone="current" label="Current" address={current} sub={saved?.county || "County unknown"} />
        <ArrowForwardRounded sx={{ fontSize: 18, color: slate[400], flexShrink: 0 }} />
        <AddressCard
          tone="new"
          label="New"
          address={newLabel}
          sub={draft?.county || "County set after verification"}
        />
      </Flx>

      {changes.length ? (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "8px", background: "#fff", overflow: "hidden" }}>
          {changes.map(([k, label], i) => (
            <Flx
              key={k}
              ac
              sx={{ gap: 2, px: 1.75, py: 1.1, borderTop: i ? `1px solid ${LINE}` : "none", fontSize: 13 }}
            >
              <Box sx={{ flex: "0 0 130px", color: slate[600] }}>{label}</Box>
              <Flx ac wrap sx={{ gap: "6px", minWidth: 0 }}>
                <Box component="span" sx={{ color: slate[400], textDecoration: "line-through" }}>
                  {before[k] || "—"}
                </Box>
                <Box component="span" sx={{ color: slate[500] }}>→</Box>
                <Box
                  component="span"
                  sx={{ px: "6px", borderRadius: "4px", background: teal[100], color: teal[800], fontWeight: 500 }}
                >
                  {draft?.[k] || "—"}
                </Box>
              </Flx>
            </Flx>
          ))}
        </Box>
      ) : (
        <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
          No field changes — saving re-runs verification on the same address.
        </Txt>
      )}

      {draft && !draft.verified ? (
        <Flx
          ac
          sx={{
            gap: "8px",
            px: 1.75,
            py: 1.25,
            borderRadius: "8px",
            background: amber.light,
            color: amber.chipText,
            fontSize: 13,
          }}
        >
          <ErrorOutlineRounded sx={{ fontSize: 17, flexShrink: 0 }} />
          <span>
            <strong>No geocode match.</strong> You can still save it as entered. It will be marked as not verified, and the map
            pin will be the geocoder’s closest guess.
          </span>
        </Flx>
      ) : null}

      {titleMatches ? (
        <Flx
          sx={{
            gap: 1,
            px: 1,
            py: 1,
            border: `1px solid ${LINE}`,
            borderRadius: "8px",
            background: "#fff",
            cursor: "pointer",
          }}
          onClick={() => setAlsoTitle((v) => !v)}
        >
          <Checkbox
            size="small"
            checked={alsoTitle}
            onChange={(e) => setAlsoTitle(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            sx={{ p: "2px" }}
            inputProps={{ "aria-label": "Also update the address on title" }}
          />
          <Flx column sx={{ gap: "2px", pt: "2px" }}>
            <Txt component="span" sx={{ fontSize: 13.5, color: ink }}>
              Also update the address on title
            </Txt>
            <Txt component="span" sx={{ fontSize: 12.5, color: slate[500] }}>
              It currently matches the official address. Leave unchecked if title still shows the old address.
            </Txt>
          </Flx>
        </Flx>
      ) : null}

      {errorLine}

      <Flx ac jb sx={{ gap: 2 }}>
        <TextLink
          disabled={busy}
          onClick={() => {
            setError(null);
            setStep(draft?.source === "search" ? "search" : "manual");
          }}
        >
          Back to edit
        </TextLink>
        <Flx ac sx={{ gap: 1 }}>
          <Button variant="outlined" onClick={cancel} disabled={busy} sx={neutralBtnSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={busy}>
            {busy ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : draft?.verified ? (
              "Save address"
            ) : (
              "Save without verification"
            )}
          </Button>
        </Flx>
      </Flx>
    </Box>
  );
};

PropertyOfficialAddress.propTypes = {
  property: PropTypes.object,
  loanId: PropTypes.string,
  onEditingChange: PropTypes.func,
};

export default PropertyOfficialAddress;
